import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  Search,
  Filter,
  Eye,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Maximize2,
  Minimize2,
  X,
  ArrowLeft,
  User,
  Mail,
  Phone,
  FileText,
  Calendar,
  Sparkles,
  Shield,
  History,
  Check,
  CornerDownRight,
  ExternalLink
} from 'lucide-react';
import { KYCDocumentType, KYCStatus, KYCSubmission, KYCAuditEntry } from '../types';
import { getAdminToken } from '../lib/paymentService';

const PRESET_REASONS = [
  'Front side is blurry or unreadable. Please provide a clear, sharp photo.',
  'Back side address page is missing or obscured by flash glare.',
  'Document expired or date of birth does not meet minimum age verification.',
  'Legal name on ID does not match the registered user name.',
  'Face selfie does not match the photograph on the official government ID.',
  'Document corners are cropped out. All 4 borders must be fully visible.',
];

export const AdminKYCReviewPage: React.FC = () => {
  const navigate = useNavigate();

  // Admin user identification
  const adminEmail = localStorage.getItem('pairup_admin_user') || 'rahulfilmsindia@gmail.com';

  // Submissions and Stats State
  const [submissions, setSubmissions] = useState<KYCSubmission[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    reuploadRequired: 0,
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Filters
  const [roleQueueFilter, setRoleQueueFilter] = useState<'ALL' | 'client' | 'companion'>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [docTypeFilter, setDocTypeFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [roleCounts, setRoleCounts] = useState<{ all: number; client: number; companion: number }>({
    all: 0,
    client: 0,
    companion: 0,
  });

  // Selected Submission for In-depth Review Modal
  const [selectedKyc, setSelectedKyc] = useState<KYCSubmission | null>(null);
  const [activeViewerTab, setActiveViewerTab] = useState<'FRONT' | 'BACK' | 'SELFIE'>('FRONT');

  // Secure Document Viewer Controls
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [rotationDegrees, setRotationDegrees] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Status Updater Form State
  const [actionChoice, setActionChoice] = useState<'APPROVE' | 'REJECT' | 'REUPLOAD_REQUIRED'>('APPROVE');
  const [reasonText, setReasonText] = useState<string>('');
  const [isSubmittingReview, setIsSubmittingReview] = useState<boolean>(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewSuccessMessage, setReviewSuccessMessage] = useState<string | null>(null);

  // Fetch KYC Submissions from Backend
  const fetchSubmissions = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      const adminToken = getAdminToken();
      if (!adminToken) throw new Error("Admin session missing. Please sign in again.");
      const res = await fetch(`/api/admin/kyc/documents`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error('Failed to load KYC submissions from server');

      const data = await res.json();
      if (data.success) {
        const documents = (data.documents || []).map((doc: any) => ({
          id: doc.id,
          userId: doc.userId,
          userName: doc.userName || doc.userId,
          userEmail: doc.userEmail || '',
          userPhone: doc.userPhone || '',
          userRole: doc.userRole || 'unknown',
          documentType: doc.documentType,
          frontImageUrl: doc.frontPreviewUrl,
          backImageUrl: doc.backPreviewUrl || undefined,
          status: doc.status === 'VERIFIED' ? 'APPROVED' : doc.status === 'UNDER_REVIEW' ? 'PENDING_REVIEW' : doc.status,
          rejectionReason: doc.rejectionReason || undefined,
          submittedAt: doc.updatedAt || doc.createdAt,
          updatedAt: doc.updatedAt || doc.createdAt,
          auditTrail: [],
        }));
        const roleFiltered = documents.filter((doc: any) =>
          roleQueueFilter === 'ALL' || doc.userRole === roleQueueFilter
        );
        const normalized = roleFiltered.filter((doc: any) =>
          (statusFilter === 'ALL' || doc.status === statusFilter || (statusFilter === 'PENDING_REVIEW' && doc.status === 'SUBMITTED')) &&
          (docTypeFilter === 'ALL' || doc.documentType === docTypeFilter) &&
          (!searchQuery.trim() || `${doc.id} ${doc.userId} ${doc.userName} ${doc.userEmail} ${doc.documentType}`.toLowerCase().includes(searchQuery.trim().toLowerCase()))
        );
        setSubmissions(normalized as KYCSubmission[]);
        setRoleCounts({
          all: documents.length,
          client: documents.filter((doc: any) => doc.userRole === 'client').length,
          companion: documents.filter((doc: any) => doc.userRole === 'companion').length,
        });
        setStats({
          total: roleFiltered.length,
          pending: roleFiltered.filter((doc: any) => doc.status === 'PENDING_REVIEW' || doc.status === 'SUBMITTED').length,
          approved: roleFiltered.filter((doc: any) => doc.status === 'APPROVED').length,
          rejected: roleFiltered.filter((doc: any) => doc.status === 'REJECTED').length,
          reuploadRequired: roleFiltered.filter((doc: any) => doc.status === 'REUPLOAD_REQUIRED').length,
        });
      }
    } catch (err: any) {
      setFetchError(err.message || 'Error fetching submissions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, [roleQueueFilter, statusFilter, docTypeFilter]);

  // Handle Search Trigger
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchSubmissions();
  };

  // Open Review Modal for a submission
  const handleOpenReview = async (kyc: KYCSubmission) => {
    const adminToken = getAdminToken();
    if (!adminToken) {
      setFetchError('Admin session missing. Please sign in again.');
      return;
    }
    try {
      const loadPreview = async (url?: string) => {
        if (!url) return undefined;
        const response = await fetch(url, { headers: { Authorization: `Bearer ${adminToken}` } });
        if (!response.ok) throw new Error('Unable to securely load the KYC document.');
        return URL.createObjectURL(await response.blob());
      };
      const [frontImageUrl, backImageUrl] = await Promise.all([
        loadPreview(kyc.frontImageUrl), loadPreview(kyc.backImageUrl),
      ]);
      setSelectedKyc({ ...kyc, frontImageUrl: frontImageUrl || '', backImageUrl });
    } catch (error: any) {
      setFetchError(error.message || 'Unable to load KYC document.');
      return;
    }
    setActiveViewerTab('FRONT');
    setZoomLevel(1);
    setRotationDegrees(0);
    setActionChoice(kyc.status === 'APPROVED' ? 'APPROVE' : kyc.status === 'REJECTED' ? 'REJECT' : 'APPROVE');
    setReasonText(kyc.rejectionReason || kyc.reuploadNotes || '');
    setReviewError(null);
    setReviewSuccessMessage(null);
  };

  // Close Review Modal
  const handleCloseReview = () => {
    if (selectedKyc?.frontImageUrl.startsWith('blob:')) URL.revokeObjectURL(selectedKyc.frontImageUrl);
    if (selectedKyc?.backImageUrl?.startsWith('blob:')) URL.revokeObjectURL(selectedKyc.backImageUrl);
    setSelectedKyc(null);
    setIsFullscreen(false);
  };

  // Submit Status Update
  const handleSubmitReview = async () => {
    if (!selectedKyc) return;
    setReviewError(null);
    setReviewSuccessMessage(null);

    // Rejection / Reupload reason validation
    if ((actionChoice === 'REJECT' || actionChoice === 'REUPLOAD_REQUIRED') && !reasonText.trim()) {
      setReviewError(`A clear explanation is required when setting status to ${actionChoice}.`);
      return;
    }

    setIsSubmittingReview(true);
    try {
      const payload = {
        documentId: selectedKyc.id,
        status:
          actionChoice === 'APPROVE'
            ? 'VERIFIED'
            : actionChoice === 'REUPLOAD_REQUIRED'
            ? 'REUPLOAD_REQUIRED'
            : 'REJECTED',
        rejectionReason: reasonText.trim(),
      };

      const adminToken = getAdminToken();
      if (!adminToken) throw new Error("Admin session missing. Please sign in again.");
      const res = await fetch(`/api/admin/kyc/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error?.message || 'Failed to update KYC status.');
      }

      setReviewSuccessMessage(`Document ${selectedKyc.id} updated to ${data.document.status}.`);

      // Refresh list
      fetchSubmissions();
    } catch (err: any) {
      setReviewError(err.message || 'Error updating status. Please try again.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans pb-16">
      {/* Top Header */}
      <header className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
              title="Return to Admin Panel"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-purple-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-purple-950">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-base sm:text-lg font-black text-white tracking-tight flex items-center gap-2">
                  Admin KYC & Government ID Review
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono font-bold border border-purple-500/30">
                    Master Spec v1.0
                  </span>
                </h1>
                <p className="text-[11px] text-slate-400 hidden sm:block">
                  Aadhaar, PAN, Driving Licence, and Passport verification desk with audit logging
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchSubmissions}
              className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-bold text-slate-200 flex items-center gap-1.5 transition cursor-pointer border border-slate-700"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <div className="text-xs px-2.5 py-1 rounded-xl bg-slate-800/80 border border-slate-700 text-slate-300 font-mono hidden md:block">
              Admin: <strong className="text-purple-400">{adminEmail.split('@')[0]}</strong>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        {/* SEPARATE CLIENT VS COMPANION DOCUMENT VERIFICATION QUEUE TABS */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-2 mb-6 flex flex-wrap items-center gap-2 shadow-lg">
          {[
            { id: 'client' as const, label: 'Client Document Verification', count: roleCounts.client },
            { id: 'companion' as const, label: 'Companion Document Verification', count: roleCounts.companion },
            { id: 'ALL' as const, label: 'All Verification Submissions', count: roleCounts.all },
          ].map((queue) => {
            const active = roleQueueFilter === queue.id;
            return (
              <button
                key={queue.id}
                type="button"
                onClick={() => setRoleQueueFilter(queue.id)}
                className={`px-4 py-2.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-2 ${
                  active
                    ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-md shadow-purple-950'
                    : 'bg-slate-950/70 text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800'
                }`}
              >
                <ShieldCheck className="w-4 h-4" />
                <span>{queue.label}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-mono ${
                    active ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {queue.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* STATS OVERVIEW CARDS */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-lg">
            <span className="text-xs text-slate-400 font-bold block mb-1">Total Submissions</span>
            <span className="text-2xl font-black text-white font-mono">{stats.total}</span>
          </div>

          <div className="bg-gradient-to-br from-amber-950/40 to-slate-900 border border-amber-500/30 rounded-2xl p-4 shadow-lg">
            <span className="text-xs text-amber-300 font-bold block mb-1 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Pending Review
            </span>
            <span className="text-2xl font-black text-amber-400 font-mono">{stats.pending}</span>
          </div>

          <div className="bg-gradient-to-br from-emerald-950/40 to-slate-900 border border-emerald-500/30 rounded-2xl p-4 shadow-lg">
            <span className="text-xs text-emerald-300 font-bold block mb-1 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Approved
            </span>
            <span className="text-2xl font-black text-emerald-400 font-mono">{stats.approved}</span>
          </div>

          <div className="bg-gradient-to-br from-purple-950/40 to-slate-900 border border-purple-500/30 rounded-2xl p-4 shadow-lg">
            <span className="text-xs text-purple-300 font-bold block mb-1 flex items-center gap-1">
              <RefreshCw className="w-3.5 h-3.5" /> Re-upload Req.
            </span>
            <span className="text-2xl font-black text-purple-400 font-mono">{stats.reuploadRequired}</span>
          </div>

          <div className="bg-gradient-to-br from-rose-950/40 to-slate-900 border border-rose-500/30 rounded-2xl p-4 shadow-lg col-span-2 sm:col-span-1">
            <span className="text-xs text-rose-300 font-bold block mb-1 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" /> Rejected
            </span>
            <span className="text-2xl font-black text-rose-400 font-mono">{stats.rejected}</span>
          </div>
        </div>

        {/* FILTERS & SEARCH TOOLBAR */}
        <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 mb-6 shadow-xl flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Status Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
            {[
              { id: 'ALL', label: 'All Status' },
              { id: 'PENDING_REVIEW', label: 'Pending Review', count: stats.pending },
              { id: 'APPROVED', label: 'Approved', count: stats.approved },
              { id: 'REUPLOAD_REQUIRED', label: 'Re-upload', count: stats.reuploadRequired },
              { id: 'REJECTED', label: 'Rejected', count: stats.rejected },
            ].map((tab) => {
              const isActive = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                    isActive
                      ? 'bg-purple-600 text-white shadow-md shadow-purple-950'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <span>{tab.label}</span>
                  {tab.count !== undefined && (
                    <span
                      className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                        isActive ? 'bg-purple-800 text-white' : 'bg-slate-800 text-slate-400'
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Doc Type & Search */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            <select
              value={docTypeFilter}
              onChange={(e) => setDocTypeFilter(e.target.value)}
              className="px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-hidden focus:border-purple-500 cursor-pointer"
            >
              <option value="ALL">All Documents</option>
              <option value="AADHAAR">Aadhaar Card (দুই পাশ)</option>
              <option value="VOTER_ID">Voter ID (Front + Back)</option>
              <option value="PAN">PAN Card (Front Only)</option>
              <option value="DRIVING_LICENSE">Driving Licence</option>
              <option value="PASSPORT">Passport</option>
            </select>

            <form onSubmit={handleSearchSubmit} className="relative flex-1 md:w-64">
              <input
                type="text"
                placeholder="Search user, ID, email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-purple-500 transition"
              />
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
            </form>
          </div>
        </div>

        {/* SUBMISSIONS TABLE */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-950/70 border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3.5 px-4">KYC ID</th>
                  <th className="py-3.5 px-4">User Details</th>
                  <th className="py-3.5 px-4">Document Type</th>
                  <th className="py-3.5 px-4">Sides Uploaded</th>
                  <th className="py-3.5 px-4">Submitted Date</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-purple-400" />
                      <span>Loading pending and reviewed documents...</span>
                    </td>
                  </tr>
                ) : submissions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-slate-400">
                      <Shield className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                      <p className="font-bold text-slate-300">No KYC submissions found</p>
                      <p className="text-[11px] text-slate-500 mt-1">Try clearing or changing your filters</p>
                    </td>
                  </tr>
                ) : (
                  submissions.map((item) => {
                    const isPending = item.status === 'PENDING_REVIEW' || item.status === 'SUBMITTED';
                    return (
                      <tr
                        key={item.id}
                        className={`hover:bg-slate-800/40 transition-colors ${
                          isPending ? 'bg-amber-950/10' : ''
                        }`}
                      >
                        <td className="py-3.5 px-4 font-mono font-bold text-purple-400">
                          {item.id}
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-white flex items-center gap-1.5">
                            <span>{item.userName}</span>
                            <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-800 text-slate-400 uppercase">
                              {item.userRole}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-400">{item.userEmail}</div>
                          {item.legalName && (
                            <div className="text-[10px] text-slate-500">Legal: {item.legalName}</div>
                          )}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="font-bold text-slate-200 font-mono">{item.documentType}</span>
                          {item.idNumber && (
                            <span className="text-[10px] text-slate-500 block font-mono">{item.idNumber}</span>
                          )}
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">Front ✓</span>
                            {item.documentType === 'PAN' ? (
                              <span className="px-1.5 py-0.5 rounded bg-slate-900 text-slate-600">No Back Req.</span>
                            ) : item.backImageUrl ? (
                              <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">Back ✓</span>
                            ) : (
                              <span className="px-1.5 py-0.5 rounded bg-rose-950 text-rose-400">Missing Back ✗</span>
                            )}
                            {item.selfieImageUrl && (
                              <span className="px-1.5 py-0.5 rounded bg-purple-950/60 text-purple-300">Selfie ✓</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-slate-400 whitespace-nowrap">
                          {new Date(item.submittedAt).toLocaleDateString()}{' '}
                          <span className="text-[10px] text-slate-500 font-mono">
                            {new Date(item.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 whitespace-nowrap">
                          <span
                            className={`px-2.5 py-1 rounded-full text-[10px] font-bold font-mono border inline-flex items-center gap-1 ${
                              item.status === 'APPROVED'
                                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                                : item.status === 'PENDING_REVIEW' || item.status === 'SUBMITTED'
                                ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                                : item.status === 'REUPLOAD_REQUIRED'
                                ? 'bg-purple-500/15 text-purple-300 border-purple-500/30'
                                : item.status === 'REJECTED'
                                ? 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                                : 'bg-slate-800 text-slate-400 border-slate-700'
                            }`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {item.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleOpenReview(item)}
                            className={`px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-1 ml-auto ${
                              isPending
                                ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-md shadow-purple-950'
                                : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                            }`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>{isPending ? 'Review ID' : 'Inspect'}</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* IN-DEPTH REVIEW MODAL / DRAWER */}
      {selectedKyc && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 md:p-6 animate-in fade-in overflow-y-auto">
          <div
            className={`bg-slate-900 border border-slate-800 rounded-3xl w-full flex flex-col overflow-hidden shadow-2xl transition-all ${
              isFullscreen ? 'fixed inset-2 max-w-none h-[calc(100vh-16px)]' : 'max-w-6xl max-h-[92vh]'
            }`}
          >
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/70">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-purple-500/20 text-purple-400 flex items-center justify-center border border-purple-500/30">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-black text-white">KYC Review Desk: {selectedKyc.id}</h2>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300 font-mono font-bold">
                      {selectedKyc.documentType}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400">
                    User: <strong className="text-white">{selectedKyc.userName}</strong> ({selectedKyc.userEmail}) • Role: {selectedKyc.userRole}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition cursor-pointer"
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={handleCloseReview}
                  className="p-2 rounded-xl bg-slate-800 hover:bg-rose-600 text-slate-300 hover:text-white transition cursor-pointer"
                  title="Close Review Desk"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Modal Body: Left = Secure Document Viewer, Right = Status Updater & Audit Trail */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
              {/* LEFT: SECURE DOCUMENT VIEWER (lg:col-span-7) */}
              <div className="lg:col-span-7 p-4 sm:p-5 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-slate-800 bg-slate-950 overflow-y-auto">
                <div>
                  {/* Document View Switcher Tabs */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 text-xs font-bold">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveViewerTab('FRONT');
                          setZoomLevel(1);
                          setRotationDegrees(0);
                        }}
                        className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                          activeViewerTab === 'FRONT'
                            ? 'bg-purple-600 text-white shadow-xs'
                            : 'text-slate-400 hover:text-white'
                        }`}
                      >
                        Front Side (সামনে)
                      </button>

                      {selectedKyc.documentType !== 'PAN' && (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveViewerTab('BACK');
                            setZoomLevel(1);
                            setRotationDegrees(0);
                          }}
                          className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                            activeViewerTab === 'BACK'
                              ? 'bg-purple-600 text-white shadow-xs'
                              : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          Back Side (পেছনে)
                        </button>
                      )}

                      {selectedKyc.selfieImageUrl && (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveViewerTab('SELFIE');
                            setZoomLevel(1);
                            setRotationDegrees(0);
                          }}
                          className={`px-3 py-1.5 rounded-lg transition cursor-pointer ${
                            activeViewerTab === 'SELFIE'
                              ? 'bg-purple-600 text-white shadow-xs'
                              : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          Face Match Selfie
                        </button>
                      )}
                    </div>

                    {/* Viewer Controls */}
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setZoomLevel((z) => Math.min(z + 0.25, 3))}
                        className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs cursor-pointer border border-slate-800"
                        title="Zoom In"
                      >
                        <ZoomIn className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setZoomLevel((z) => Math.max(z - 0.25, 0.5))}
                        className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs cursor-pointer border border-slate-800"
                        title="Zoom Out"
                      >
                        <ZoomOut className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setRotationDegrees((r) => (r + 90) % 360)}
                        className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs cursor-pointer border border-slate-800"
                        title="Rotate 90°"
                      >
                        <RotateCw className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setZoomLevel(1);
                          setRotationDegrees(0);
                        }}
                        className="px-2 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 text-xs font-mono cursor-pointer border border-slate-800"
                      >
                        100%
                      </button>
                    </div>
                  </div>

                  {/* Viewer Display Surface */}
                  <div className="relative rounded-2xl bg-black border border-slate-800 h-[380px] sm:h-[430px] flex items-center justify-center overflow-hidden group select-none">
                    {activeViewerTab === 'FRONT' && selectedKyc.frontImageUrl && (
                      selectedKyc.frontImageUrl.startsWith('data:application/pdf') ? (
                        <iframe
                          src={selectedKyc.frontImageUrl}
                          title="Government ID Front (PDF)"
                          className="w-full h-full bg-white rounded-lg"
                        />
                      ) : (
                        <div
                          style={{
                            transform: `scale(${zoomLevel}) rotate(${rotationDegrees}deg)`,
                            transition: 'transform 0.2s ease-out',
                          }}
                          className="relative max-w-full max-h-full flex items-center justify-center"
                        >
                          <img
                            src={selectedKyc.frontImageUrl}
                            alt="Government ID Front"
                            className="max-h-[380px] object-contain rounded-lg shadow-2xl"
                          />
                          {/* Security Watermark */}
                          <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-35">
                            <span className="text-sm sm:text-base font-mono font-black text-rose-400 rotate-[-15deg] uppercase tracking-widest border border-rose-400 px-3 py-1">
                              PAIRUP CONFIDENTIAL • AUDIT LOGGED
                            </span>
                          </div>
                        </div>
                      )
                    )}

                    {activeViewerTab === 'BACK' && (
                      selectedKyc.backImageUrl ? (
                        selectedKyc.backImageUrl.startsWith('data:application/pdf') ? (
                          <iframe
                            src={selectedKyc.backImageUrl}
                            title="Government ID Back (PDF)"
                            className="w-full h-full bg-white rounded-lg"
                          />
                        ) : (
                          <div
                            style={{
                              transform: `scale(${zoomLevel}) rotate(${rotationDegrees}deg)`,
                              transition: 'transform 0.2s ease-out',
                            }}
                            className="relative max-w-full max-h-full flex items-center justify-center"
                          >
                            <img
                              src={selectedKyc.backImageUrl}
                              alt="Government ID Back"
                              className="max-h-[380px] object-contain rounded-lg shadow-2xl"
                            />
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-35">
                              <span className="text-sm sm:text-base font-mono font-black text-rose-400 rotate-[-15deg] uppercase tracking-widest border border-rose-400 px-3 py-1">
                                PAIRUP CONFIDENTIAL • AUDIT LOGGED
                              </span>
                            </div>
                          </div>
                        )
                      ) : (
                        <div className="text-center text-slate-500 text-xs p-6">
                          <AlertCircle className="w-8 h-8 mx-auto mb-2 text-rose-400" />
                          <p className="font-bold text-slate-300">Back Side Not Uploaded</p>
                          <p className="text-[11px] text-slate-500 mt-1">This document type requires both front and back images.</p>
                        </div>
                      )
                    )}

                    {activeViewerTab === 'SELFIE' && (
                      selectedKyc.selfieImageUrl ? (
                        <div
                          style={{
                            transform: `scale(${zoomLevel}) rotate(${rotationDegrees}deg)`,
                            transition: 'transform 0.2s ease-out',
                          }}
                          className="relative max-w-full max-h-full flex items-center justify-center"
                        >
                          <img
                            src={selectedKyc.selfieImageUrl}
                            alt="Face Selfie"
                            className="max-h-[380px] object-contain rounded-2xl shadow-2xl border-2 border-purple-500/40"
                          />
                          <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-30">
                            <span className="text-xs font-mono font-black text-white rotate-[-15deg] uppercase tracking-widest border border-white px-2 py-0.5">
                              FACE LIVENESS MATCH
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center text-slate-500 text-xs p-6">
                          <User className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                          <p className="font-bold text-slate-300">No Face Selfie Provided</p>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Document Metadata Bar */}
                <div className="mt-3 p-3 rounded-xl bg-slate-900 border border-slate-800 text-[11px] text-slate-400 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    Legal Name on File: <strong className="text-white">{selectedKyc.legalName || selectedKyc.userName}</strong>
                  </div>
                  <div>
                    ID Number: <strong className="text-purple-400 font-mono">{selectedKyc.idNumber || 'Not Specified'}</strong>
                  </div>
                  <div>
                    Protection: <strong className="text-emerald-400">Vault Encrypted</strong>
                  </div>
                </div>
              </div>

              {/* RIGHT: STATUS UPDATER & AUDIT LOGGING (lg:col-span-5) */}
              <div className="lg:col-span-5 p-4 sm:p-5 flex flex-col justify-between overflow-y-auto bg-slate-900/60">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-black text-white flex items-center gap-1.5">
                      <Shield className="w-4 h-4 text-purple-400" />
                      <span>Compliance Officer Decision</span>
                    </h3>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Select status and provide mandatory reason for rejection or re-upload.
                    </p>
                  </div>

                  {/* Status Decision Buttons */}
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setActionChoice('APPROVE')}
                      className={`p-3 rounded-xl border text-xs font-bold transition cursor-pointer flex flex-col items-center justify-center gap-1 ${
                        actionChoice === 'APPROVE'
                          ? 'bg-emerald-600 text-white border-emerald-500 shadow-md shadow-emerald-950'
                          : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-white'
                      }`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>APPROVE</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActionChoice('REUPLOAD_REQUIRED')}
                      className={`p-3 rounded-xl border text-xs font-bold transition cursor-pointer flex flex-col items-center justify-center gap-1 ${
                        actionChoice === 'REUPLOAD_REQUIRED'
                          ? 'bg-purple-600 text-white border-purple-500 shadow-md shadow-purple-950'
                          : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-white'
                      }`}
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span>RE-UPLOAD</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setActionChoice('REJECT')}
                      className={`p-3 rounded-xl border text-xs font-bold transition cursor-pointer flex flex-col items-center justify-center gap-1 ${
                        actionChoice === 'REJECT'
                          ? 'bg-rose-600 text-white border-rose-500 shadow-md shadow-rose-950'
                          : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-white'
                      }`}
                    >
                      <AlertCircle className="w-4 h-4" />
                      <span>REJECT</span>
                    </button>
                  </div>

                  {/* Rejection / Reupload Reason Field */}
                  <div>
                    <label className="block text-xs font-bold text-slate-300 mb-1">
                      Reason / Review Notes{' '}
                      {actionChoice !== 'APPROVE' && <span className="text-rose-400">* (Mandatory)</span>}
                    </label>
                    <textarea
                      rows={3}
                      value={reasonText}
                      onChange={(e) => setReasonText(e.target.value)}
                      placeholder={
                        actionChoice === 'APPROVE'
                          ? 'Optional approval note (e.g. Verified against state transport database)'
                          : 'Explain clearly what needs to be fixed or why the document was rejected...'
                      }
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-purple-500 transition"
                    />

                    {/* Preset reason quick chips */}
                    {actionChoice !== 'APPROVE' && (
                      <div className="mt-2">
                        <span className="text-[10px] text-slate-400 font-bold block mb-1">Quick Presets:</span>
                        <div className="flex flex-wrap gap-1">
                          {PRESET_REASONS.map((preset, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setReasonText(preset)}
                              className="text-[10px] px-2 py-0.5 rounded-lg bg-slate-950 border border-slate-800 text-slate-400 hover:text-white hover:border-slate-700 transition cursor-pointer text-left"
                            >
                              + {preset.substring(0, 32)}...
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Error & Success Alerts */}
                  {reviewError && (
                    <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
                      <span>{reviewError}</span>
                    </div>
                  )}

                  {reviewSuccessMessage && (
                    <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                      <span>{reviewSuccessMessage}</span>
                    </div>
                  )}

                  {/* Submit Review Action Button */}
                  <button
                    type="button"
                    disabled={isSubmittingReview}
                    onClick={handleSubmitReview}
                    className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-lg ${
                      actionChoice === 'APPROVE'
                        ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-950'
                        : actionChoice === 'REUPLOAD_REQUIRED'
                        ? 'bg-purple-600 hover:bg-purple-500 text-white shadow-purple-950'
                        : 'bg-rose-600 hover:bg-rose-500 text-white shadow-rose-950'
                    }`}
                  >
                    {isSubmittingReview ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Logging Decision & Notifying User...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Confirm {actionChoice.replace('_', ' ')} Decision</span>
                      </>
                    )}
                  </button>

                  {/* AUDIT LOG REQUIREMENTS TRAIL (MASTER SPEC REQUIREMENT) */}
                  <div className="pt-3 border-t border-slate-800">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-white flex items-center gap-1.5">
                        <History className="w-3.5 h-3.5 text-purple-400" />
                        <span>KYC Audit Trail ({selectedKyc.auditTrail?.length || 0} entries)</span>
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">Immutable Compliance Log</span>
                    </div>

                    <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                      {selectedKyc.auditTrail && selectedKyc.auditTrail.length > 0 ? (
                        selectedKyc.auditTrail.map((log: KYCAuditEntry, idx: number) => (
                          <div
                            key={idx}
                            className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 text-[11px] space-y-1"
                          >
                            <div className="flex items-center justify-between text-[10px]">
                              <span className="font-bold text-purple-300 font-mono">{log.action}</span>
                              <span className="text-slate-500 font-mono">
                                {new Date(log.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <div className="text-slate-300">
                              <span className="text-slate-500">By:</span> {log.adminEmail || log.adminId} •{' '}
                              <span className="text-slate-500">Status:</span> {log.prevStatus} →{' '}
                              <strong className="text-white">{log.newStatus}</strong>
                            </div>
                            {log.reason && (
                              <div className="text-slate-400 italic bg-slate-900/80 p-1 rounded text-[10px]">
                                "{log.reason}"
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-slate-500 text-[11px] py-3">
                          No audit trail records yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminKYCReviewPage;
